import "react-toastify/dist/ReactToastify.css";
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { getHomePath, isSuperAdmin } from "./utils/accountType";
import AddClient from "./pages/AddClient";
import ChangePassword from "./pages/changePassword";
import ClientDetails from "./pages/ClientDetails";
import Clients from "./pages/Clients";
import ComingSoon from "./pages/ComingSoon";
import Dashboard from "./pages/Dashboard";
import ForgotPassword from "./pages/ForgotPassword";
import Header from "./components/Header";
import Login from "./pages/Login";
import Logout from "./pages/Logout";
import Sidebar from "./components/Sidebar";
import Staff from "./pages/Staff";
import StaffDetails from "./pages/StaffDetails";
import SuperAdminActivityLogDetails from "./pages/SuperAdminActivityLogDetails";
import SuperAdminActivityLogs from "./pages/SuperAdminActivityLogs";
import SuperAdminDashboard from "./pages/SuperAdminDashboard";
import SuperAdminOrganizationDetails from "./pages/SuperAdminOrganizationDetails";
import SuperAdminOrganizations from "./pages/SuperAdminOrganizations";
import SuperAdminSidebar from "./components/SuperAdminSidebar";
import SuperAdminUserDetails from "./pages/SuperAdminUserDetails";
import SuperAdminUsers from "./pages/SuperAdminUsers";
import UpdatePasswordBusiness from "./pages/UpdatePasswordBusiness";
import ImportDetails from "./pages/ImportDetails";
import Validate from "./pages/Validate";
import ValidationDetails from "./pages/ValidationDetails";
import VisitDetails from "./pages/VisitDetails";
import Visits from "./pages/Visits";

const ORG_ONLY_PREFIXES = ["/change-password", "/clients", "/encounter-forms", "/reports", "/staff", "/validate", "/visits"];
const SUPER_ONLY_PREFIXES = ["/organizations", "/users", "/activity-logs"];

const pathStartsWithAny = (pathname, prefixes) =>
	prefixes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));

const Layout = ({ isAuth, user }) => {
	const authRoutes = ["/", "/forgot-password", "/login", "/update-password"];
	const location = useLocation();

	const isAuthRoute = authRoutes.includes(location.pathname);
	const isSuperAdminSession = isSuperAdmin(user);

	const homePath = getHomePath();

	const [isSidebarOpen, setIsSidebarOpen] = useState(false);

	const closeSidebar = useCallback(() => {
		setIsSidebarOpen(false);
	}, []);

	const openSidebar = useCallback(() => {
		setIsSidebarOpen(true);
	}, []);

	useEffect(() => {
		const handleResize = () => {
			if (window.innerWidth > 900) setIsSidebarOpen(false);
		};
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	useEffect(() => {
		document.body.style.overflow = isSidebarOpen ? "hidden" : "";
		return () => {
			document.body.style.overflow = "";
		};
	}, [isSidebarOpen]);

	if (isAuthRoute) {
		return (
			<div style={{ minHeight: "100vh" }}>
				<Routes>
					<Route element={<ForgotPassword />} path="/forgot-password" />
					<Route element={<Login />} path="/login" />
					<Route element={<Logout />} path="/logout" />
					<Route
						element={user?.is_first_login ? <UpdatePasswordBusiness /> : <Navigate to={homePath} />}
						path="/update-password"
					/>
					<Route element={<RedirectToHomeOrLogin homePath={homePath} isAuth={isAuth} />} path="/" />
				</Routes>
			</div>
		);
	}

	if (isAuth && isSuperAdminSession && pathStartsWithAny(location.pathname, ORG_ONLY_PREFIXES)) {
		return <Navigate replace to={homePath} />;
	}

	if (isAuth && !isSuperAdminSession && pathStartsWithAny(location.pathname, SUPER_ONLY_PREFIXES)) {
		return <Navigate replace to={homePath} />;
	}

	return (
		<div className="app-shell">
			{isAuth ? (
				<>
					{isSuperAdminSession ? (
						<SuperAdminSidebar isOpen={isSidebarOpen} onClose={closeSidebar} />
					) : (
						<Sidebar isOpen={isSidebarOpen} onClose={closeSidebar} />
					)}
					<Logout />
					<div className="app-main">
						<div className="app-topbar">
							<Header onMenuClick={openSidebar} />
						</div>
						<div className="app-content">
							<Routes>
								<Route
									element={
										<PrivateRoute isAuth={isAuth}>
											{isSuperAdminSession ? <SuperAdminDashboard /> : <Dashboard />}
										</PrivateRoute>
									}
									path="/dashboard"
								/>
								<Route
									element={
										<PrivateRoute isAuth={isAuth}>
											<SuperAdminOrganizations />
										</PrivateRoute>
									}
									path="/organizations"
								/>
								<Route
									element={
										<PrivateRoute isAuth={isAuth}>
											<SuperAdminOrganizationDetails />
										</PrivateRoute>
									}
									path="/organizations/:id"
								/>
								<Route
									element={
										<PrivateRoute isAuth={isAuth}>
											<SuperAdminUsers />
										</PrivateRoute>
									}
									path="/users"
								/>
								<Route
									element={
										<PrivateRoute isAuth={isAuth}>
											<SuperAdminUserDetails />
										</PrivateRoute>
									}
									path="/users/:accountType/:id"
								/>
								<Route
									element={
										<PrivateRoute isAuth={isAuth}>
											{isSuperAdminSession ? (
												<SuperAdminActivityLogs />
											) : (
												<ComingSoon
													description="Organization activity logs will appear here."
													title="Activity Logs"
												/>
											)}
										</PrivateRoute>
									}
									path="/activity-logs"
								/>
								<Route
									element={
										<PrivateRoute isAuth={isAuth}>
											{isSuperAdminSession ? (
												<SuperAdminActivityLogDetails />
											) : (
												<Navigate replace to="/activity-logs" />
											)}
										</PrivateRoute>
									}
									path="/activity-logs/:id"
								/>
								<Route
									element={
										<PrivateRoute isAuth={isAuth}>
											<ComingSoon
												description={
													isSuperAdminSession
														? "Super Admin settings will appear here."
														: "Organization settings will appear here."
												}
												title="Settings"
											/>
										</PrivateRoute>
									}
									path="/settings"
								/>
								<Route
									element={
										<PrivateRoute isAuth={isAuth}>
											<ChangePassword />
										</PrivateRoute>
									}
									path="/change-password"
								/>
								<Route
									element={
										<PrivateRoute isAuth={isAuth}>
											<Navigate to="/staff" replace />
										</PrivateRoute>
									}
									path="/staff/add"
								/>
								<Route
									element={
										<PrivateRoute isAuth={isAuth}>
											<Staff />
										</PrivateRoute>
									}
									path="/staff"
								/>
								<Route
									element={
										<PrivateRoute isAuth={isAuth}>
											<StaffDetails />
										</PrivateRoute>
									}
									path="/staff/:id"
								/>
								<Route
									element={
										<PrivateRoute isAuth={isAuth}>
											<AddClient />
										</PrivateRoute>
									}
									path="/clients/add"
								/>
								<Route
									element={
										<PrivateRoute isAuth={isAuth}>
											<Clients />
										</PrivateRoute>
									}
									path="/clients"
								/>
								<Route
									element={
										<PrivateRoute isAuth={isAuth}>
											<ClientDetails />
										</PrivateRoute>
									}
									path="/clients/:id"
								/>
								<Route
									element={
										<PrivateRoute isAuth={isAuth}>
											<Visits />
										</PrivateRoute>
									}
									path="/visits"
								/>
								<Route
									element={
										<PrivateRoute isAuth={isAuth}>
											<VisitDetails />
										</PrivateRoute>
									}
									path="/visits/:id"
								/>
								<Route
									element={
										<PrivateRoute isAuth={isAuth}>
											<Validate />
										</PrivateRoute>
									}
									path="/validate"
								/>
								<Route
									element={
										<PrivateRoute isAuth={isAuth}>
											<ImportDetails />
										</PrivateRoute>
									}
									path="/validate/imports/:runId"
								/>
								<Route
									element={
										<PrivateRoute isAuth={isAuth}>
											<ValidationDetails />
										</PrivateRoute>
									}
									path="/validate/:id"
								/>
								<Route
									element={
										<PrivateRoute isAuth={isAuth}>
											<ComingSoon
												description="Electronic encounter forms will appear here once that feature is available."
												title="Encounter Forms"
											/>
										</PrivateRoute>
									}
									path="/encounter-forms"
								/>
								<Route
									element={
										<PrivateRoute isAuth={isAuth}>
											<ComingSoon description="Organization reports will appear here." title="Reports" />
										</PrivateRoute>
									}
									path="/reports"
								/>
								<Route element={<Navigate replace to={homePath} />} path="*" />
							</Routes>
						</div>
					</div>
				</>
			) : (
				<Routes>
					<Route element={<Navigate replace to="/login" />} path="*" />
				</Routes>
			)}
		</div>
	);
};

const PrivateRoute = ({ children, isAuth }) => (isAuth ? children : <Navigate replace={true} to="/login" />);

const RedirectToHomeOrLogin = ({ homePath, isAuth }) =>
	isAuth ? <Navigate replace={true} to={homePath} /> : <Navigate replace={true} to="/login" />;

function App() {
	const user = useSelector((state) => state?.user);
	const isAuth = user.user?.token ? true : false;

	return (
		<>
			<ToastContainer
				autoClose={5000}
				closeOnClick
				draggable
				hideProgressBar={false}
				newestOnTop={false}
				pauseOnFocusLoss
				pauseOnHover
				position="top-right"
				rtl={false}
				style={{ zIndex: 99999 }}
			/>

			<Router>
				<Layout isAuth={isAuth} user={user.user} />
			</Router>
		</>
	);
}

export default App;
